import { AnalyticsImpl } from "~/modules/telemetry/AnalyticsImpl";
import { CbmImpl } from "~/modules/cbm";
import { LoggerName, getLogger } from "~/modules/logger";
import { TaskRouterImpl } from "~/packages/taskrouter/TaskRouterImpl";
import {
    checkParticipantExists,
    checkReservationExists,
    checkWorker,
    getReservationsByTask,
    handleGetParticipantsByTask
} from "~/modules/actions/ActionUtils";
import { sendWorkerTrackingEvent, toCauseMessage } from "~/modules/telemetry/utils";
import { EVENTS } from "~/modules/telemetry/Analytics";
import { ContextInstance, ContextManager } from "~/modules/contextManager/ContextManager";
import { ErrorCode, ErrorSeverity } from "~/modules/error";
import { Action } from "~/modules/actions/Action";
import { TaskParticipant } from "~/modules/participant";
import { createFlexSdkError } from "~/modules/error/ThrowError/ErrorHelper";

































export class KickVoiceParticipant implements Action<Promise<void>> {
    


    readonly #targetParticipantSid: string;

    


    readonly #taskSid: string;

    constructor(targetParticipantSid: string, taskSid: string) {
        this.#targetParticipantSid = targetParticipantSid;
        this.#taskSid = taskSid;
    }

    async run(ctx: {}) {
        const context = ctx as ContextManager;
        const taskRouter = context.retrieveInstanceOf(ContextInstance.TaskRouter, TaskRouterImpl);
        await taskRouter.init();
        const logger = getLogger(context)(LoggerName.Actions);
        const worker = taskRouter.worker;
        const analytics = context.retrieveInstanceOf(ContextInstance.AnalyticsImpl, AnalyticsImpl);
        const cbm = context.retrieveInstanceOf(ContextInstance.CbmImpl, CbmImpl);
        await cbm.init();

        logger.debug(`kickVoiceParticipant invoked with #taskSid: ${this.#taskSid}`);

        const isWorkerSid = this.#targetParticipantSid.startsWith("WK");
        const isParticipantSid = this.#targetParticipantSid.startsWith("UT");
        if (!isWorkerSid && !isParticipantSid) {
            const errorMsg = `KickVoiceParticipant: Provided invalid participant sid ${
                this.#targetParticipantSid
            }, either worker sid or participant sid should be provided`;
            logger.error(errorMsg);
            return Promise.reject(
                createFlexSdkError(context)(
                    ErrorCode.InvalidParticipantSid,
                    { severity: ErrorSeverity.Error },
                    errorMsg
                )
            );
        }

        checkWorker(worker, {
            actionName: "KickVoiceParticipant",
            moduleName: "actions/Voice",
            logger,
            analytics,
            context,
            event: EVENTS.KickVoiceParticipantCompleted,
            extra: {
                taskSid: this.#taskSid
            },
            worker
        });

        const reservations = getReservationsByTask(worker, this.#taskSid);
        const reservation = reservations.find((res) => res.status === "accepted");

        checkReservationExists(reservation, this.#taskSid, {
            actionName: "KickVoiceParticipant",
            moduleName: "actions/Voice",
            logger,
            analytics,
            context,
            event: EVENTS.KickVoiceParticipantCompleted,
            extra: {
                taskSid: this.#taskSid,
                reservationSid: reservation?.sid,
                targetParticipantSid: this.#targetParticipantSid
            },
            worker
        });

        const task = reservation!.task;

        const participants: TaskParticipant[] = await handleGetParticipantsByTask(cbm, task, {
            actionName: "KickVoiceParticipant",
            moduleName: "actions/Voice",
            logger,
            analytics,
            context,
            event: EVENTS.KickVoiceParticipantCompleted,
            extra: {
                taskSid: this.#taskSid,
                reservationSid: reservation?.sid,
                targetParticipantSid: this.#targetParticipantSid
            },
            worker
        });

        const participant = participants.find(
            (p) =>
                p.routingProperties?.workerSid === this.#targetParticipantSid ||
                p.participantSid === this.#targetParticipantSid
        );

        checkParticipantExists(participant, this.#targetParticipantSid, this.#taskSid, {
            actionName: "KickVoiceParticipant",
            moduleName: "actions/Voice",
            logger,
            analytics,
            context,
            event: EVENTS.KickVoiceParticipantCompleted,
            extra: {
                taskSid: this.#taskSid,
                reservationSid: reservation!.sid,
                targetParticipantSid: this.#targetParticipantSid
            },
            worker
        });
        if (worker.sid === participant!.routingProperties?.workerSid) {
            const errorMsg = `KickVoiceParticipant: Worker ${worker.sid} cannot kick themselves from task ${
                this.#taskSid
            }`;
            logger.error(errorMsg);
            sendWorkerTrackingEvent(EVENTS.KickVoiceParticipantCompleted, analytics, worker, {
                error: errorMsg,
                taskSid: this.#taskSid,
                reservationSid: reservation!.sid,
                targetParticipantSid: this.#targetParticipantSid,
                participantSid: participant!.participantSid,
                participantType: participant!.type
            });
            return Promise.reject(
                createFlexSdkError(context)(ErrorCode.FailedSelfKick, { severity: ErrorSeverity.Error }, errorMsg)
            );
        }

        try {
            await cbm.removeVoiceParticipant(task, participant!.participantSid);
            sendWorkerTrackingEvent(EVENTS.KickVoiceParticipantCompleted, analytics, worker, {
                taskSid: this.#taskSid,
                targetParticipantSid: this.#targetParticipantSid,
                participantSid: participant!.participantSid,
                participantType: participant!.type
            });
            return Promise.resolve();
        } catch (error) {
            const errorMsg = `KickVoiceParticipant: Failed to kick participant ${
                this.#targetParticipantSid
            } from task ${this.#taskSid}`;
            logger.error(errorMsg, error);
            sendWorkerTrackingEvent(EVENTS.KickVoiceParticipantCompleted, analytics, worker, {
                error: `${errorMsg} (${toCauseMessage(error)})`,
                taskSid: this.#taskSid,
                reservationSid: reservation!.sid,
                targetParticipantSid: this.#targetParticipantSid,
                participantSid: participant!.participantSid,
                participantType: participant!.type
            });
            return Promise.reject(
                createFlexSdkError(context)(
                    ErrorCode.FailedToKickVoiceParticipant,
                    { severity: ErrorSeverity.Error },
                    errorMsg,
                    error as Error
                )
            );
        }
    }
}
